import { useAuthenticator } from '@aws-amplify/ui-react';
import { Box, Container, Skeleton, Link, Stack } from '@mui/material';
import { LaunchPreviewFeatureTable } from './sku_launch/FeatureTable';
import { TrialEndCard } from './sku_launch/TrialEndCard';
import { CanceledSubCard } from './sku_launch/CanceledSubCard';
import { StartTrialCard, startStripeTrial } from './sku_launch/StartTrialCard';
import { getSubscriptionStatus } from '../RequireAuth/RequireAuth';
import { useState, useEffect } from 'react';
import { createStripePortalSession } from '../Subscription/StripePortalRedirect';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useNavigate, useLocation } from 'react-router-dom';
import { useIsMobile } from '../Utils';

// The subscription component is a container for various cards associated with a SKU
// -- they either show an optionn to join a trial, handle trial expiration,  
// or subscription cancelation. 

const styles = {
    pageContainer: {
        minHeight: '100vh',
        backgroundColor: '#0d0d0d',
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
        pt: '2%'
    },
    contentContainer: {
        py: 6,
        position: 'relative',
        zIndex: 1,
        mt: '2%'
    },
    layoutBox: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        gap: 8
    },
    subscriptionBox: {
        width: { xs: '100%', sm: '300px' }, // Full width on mobile, fixed width on larger screens
        height: { xs: 'auto', sm: '400px' }, // Auto height on mobile, fixed height on larger screens
        textAlign: 'center'
    },
    stripeTableStyles: {
        '.stripe-pricing-table': {
            '--stripe-shadow': 'none',
            '--stripe-border': 'none',
            '--stripe-color-background': 'transparent',
            '--stripe-border-radius': '8px'
        }
    }
};

// This function is used to handle the management of a subscription. It will create a
// Stripe portal session for the user, which allows them to manage their subscription. 
// It is used both by the cards we show and by the topbar, which provides the ability
// to manage a subscription. 
export const handleManageSubscription = async (user, setSubscriptionLoading) => {
    if (!user?.signInUserSession?.idToken?.payload?.sub) {
        console.error('Missing required data for subscription management');
        return;
    }
    
    setSubscriptionLoading(true);
    try {
        await createStripePortalSession(user.signInUserSession.idToken.payload.sub);
    } catch (error) {
        console.error('Failed to create Stripe portal session:', error);
    } finally {
        setSubscriptionLoading(false);
    }
};


export function Subscription({ group = null }) {
    const { route, user } = useAuthenticator((context) => [context.route, context.user]);
    const [subscriptionStatus, setSubscriptionStatus] = useState(null);
    const [subscriptionLoading, setSubscriptionLoading] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const isMobile = useIsMobile();
    
    // Get the 'from' parameter to know where the user came from
    const urlParams = new URLSearchParams(location.search);
    const fromPath = urlParams.get('from');

    useEffect(() => {
        const initializeSubscription = async () => {
            try {                
                if (route !== 'authenticated') {
                    setSubscriptionStatus('unknown');
                }
                else {
                    const status = await getSubscriptionStatus(group);                    

                    // For now, we have one SKU so let's just start the trial 
                    // if they haven't already started one:
                    if (status === 'no_stripe_account' || status === 'trial_paused') {
                        
                        const data = await startStripeTrial(user);
                        setSubscriptionStatus(data.subscriptionStatus);

                        // Don't use navigate here because we want a full reload
                        // of the page. Otherwise topAppBar won't refresh its subscription
                        // status.
                        // navigate('/dashboard');
                        // window.location = '/dashboard';
                        window.location.href = fromPath;
                    }
                    else {
                        setSubscriptionStatus(status);
                    }
                }
            } catch (error) {
                console.log(error);
            }
        };
        
        initializeSubscription();
    }, [route, group]);
    

    if (route !== 'authenticated' ||
        (route === 'authenticated' && subscriptionStatus === null) ||
        (route === 'authenticated' && subscriptionStatus === 'not_stripe_elligible') ||
        (route === 'authenticated' && subscriptionStatus === 'trialing')) {
        return null;
    }    
    
    return (
        // 
        <Box sx={styles.pageContainer}>
            <Container maxWidth="lg" sx={styles.contentContainer}>
                <Box sx={{
                    display: 'flex',
                    flexDirection: isMobile? 'column' : 'row', // Stack on mobile, row on desktop
                    justifyContent: 'center',
                    alignItems: isMobile? 'center' : 'flex-start',
                    gap: { xs: 4, md: 8 } // Smaller gap on mobile
                }}>
                    {!isMobile && <LaunchPreviewFeatureTable />}
                    <Box sx={{
                        width: { xs: '100%', sm: '300px' },
                        height: { xs: 'auto', sm: '400px' },
                        textAlign: 'center'
                    }}>
                        {subscriptionStatus === undefined || subscriptionStatus === null ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                <Skeleton 
                                    variant="rectangular" 
                                    width="100%" 
                                    height="100%" 
                                    sx={{ 
                                        bgcolor: 'rgba(255, 255, 255, 0.1)',
                                        borderRadius: 1
                                    }} 
                                />
                            </Box>
                        ) : subscriptionStatus === 'no_stripe_account' ? (
                            <Box sx={styles.stripeTableStyles}>
                                <StartTrialCard />
                            </Box>
                        ) : subscriptionStatus === 'trial_over' ? (
                            <TrialEndCard 
                                isLoading={subscriptionLoading}
                            />                        
                        ) : subscriptionStatus === 'canceled' ?(
                            <CanceledSubCard 
                                isLoading={subscriptionLoading}
                            />
                        ) : subscriptionStatus === 'not_in_group' ?(
                            <Box sx={{ 
                                p: 3, 
                                textAlign: 'center', 
                                bgcolor: 'rgba(255, 255, 255, 0.1)', 
                                borderRadius: 2,
                                border: '1px solid rgba(255, 255, 255, 0.2)'
                            }}>
                                <Box sx={{ mb: 2, fontSize: '1.2rem', fontWeight: 'bold' }}>
                                    Access Restricted
                                </Box>
                                <Box sx={{ mb: 2, color: 'rgba(255, 255, 255, 0.8)' }}>
                                    Apologies, but you're attempting to access a feature that is not included in your subscription. 
                                    If you would like access or believe this is an error, please contact{' '}
                                    <Link 
                                        href="mailto:support@revelata.com" 
                                        sx={{ 
                                            color: 'rgba(255, 255, 255, 0.9)', 
                                            textDecoration: 'underline',
                                            '&:hover': { 
                                                color: 'white',
                                                textDecoration: 'underline' 
                                            }
                                        }}
                                    >
                                        support@revelata.com
                                    </Link>
                                </Box>
                            </Box>
                        ) : 
                        // This is a fall through case we don't expect, but 
                        // we want to handle in case of a some crazy combo of back button
                        // usage etc. Rather than auto-direct and risk getting into a
                        // a loop, let's just give them some links to navigate to:
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 3, textAlign: 'left' }}> Click to:
                            <Stack spacing={2} sx={{ pl: 2 }}>
                                <Link href={process.env.REACT_APP_DEEPKPI_URL + '/app'} sx={{ color: 'gray', textDecoration: 'none', '&:hover': { textDecoration: 'underline' }, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    Continue to deepKPI
                                    <ArrowForwardIcon sx={{ color: 'gray', fontSize: 16 }} />
                                </Link>
                                <Link href={process.env.REACT_APP_DEEPANALYSIS_URL + '/app'} sx={{ color: 'gray', textDecoration: 'none', '&:hover': { textDecoration: 'underline' }, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    Continue to deepAnalysis
                                    <ArrowForwardIcon sx={{ color: 'gray', fontSize: 16 }} />
                                </Link>
                                <Link href={process.env.REACT_APP_DEEPINSIGHT_URL} sx={{ color: 'gray', textDecoration: 'none', '&:hover': { textDecoration: 'underline' }, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    Continue to deepInsight
                                    <ArrowForwardIcon sx={{ color: 'gray', fontSize: 16 }} />
                                </Link>
                                <Link component="button" onClick={() => handleManageSubscription(user, setSubscriptionLoading)} sx={{ color: 'gray', textDecoration: 'none', background: 'none', border: 'none', padding: 0, font: 'inherit', cursor: 'pointer', '&:hover': { textDecoration: 'underline' }, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    Manage your Subscription
                                    <ArrowForwardIcon sx={{ color: 'gray', fontSize: 16 }} />
                                </Link>
                            </Stack>
                        </Box>                    
                    }
                    </Box>
                    {isMobile && <LaunchPreviewFeatureTable />}
                </Box>
            </Container>
        </Box>
    );
}

export default Subscription;